import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import ModelStatusFilter from "./ModelStatusFilter";
import { useData } from "../context/Context";
import ModelViewer from "./ModelViewer";
import ResponsivePagination from "react-responsive-pagination";
import { BASE_URL_FLASK } from "../services/serviceUrls";
import { tasksAPI } from "../services/api/tasksAPI";
import { handleApiError, handleApiSuccess } from "../utils/apiHelpers";

import {
  Checkbox,
  IconButton,
  Button,
  Spinner,
  Box,
  Select,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { FaRegUser } from "react-icons/fa";
import { MdVerifiedUser } from "react-icons/md";
import toast from "react-hot-toast";

const MainModels = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [alignmentFilter, setAlignmentFilter] = useState("All"); // Changed from notAligned
  const [searchUid, setSearchUid] = useState(""); // <-- Add search state
  const [progress, setProgress] = useState(0); // Progress for CSV generation
  const [csvLoading, setCsvLoading] = useState(false); // Loading state for CSV
  const [verifyLoading, setVerifyLoading] = useState(false); // Loading state for verify
  const [models, setModels] = useState([]); // Local state for models
  const [loading, setLoading] = useState(false); // Local loading state
  const [isVerifying, setIsVerifying] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectFeedback, setRejectFeedback] = useState("");
  const [selectedModelForReject, setSelectedModelForReject] = useState(null);

  const navigate = useNavigate();
  const { taskId } = useParams(); // Get taskId from URL
  const username = localStorage.getItem("username");
  const userRole = localStorage.getItem("user_role");

  const handlePageChange = async (page) => {
    setCurrentPage(page);
    // await fetchData(page);
  };

  const {
    state: {
      models: contextModels,
      selected_status,
      selected_index,
      selected_model,
      selected_table,
      selected_task_type,
      selected_task_id,
    },
    setState,
  } = useData();

  const fetchTaskData = useCallback(async (taskId) => {
    if (!taskId) return;

    setLoading(true);

    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${BASE_URL_FLASK}/user/tasks/${taskId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      // Handle different possible response structures
      let modelsData = [];
      if (result.assigned_task_ids && Array.isArray(result.assigned_task_ids)) {
        modelsData = result.assigned_task_ids;
      } else if (result.models) {
        modelsData = result.models;
      } else if (result.data) {
        modelsData = result.data;
      } else if (Array.isArray(result)) {
        modelsData = result;
      } else {
        console.warn("Unexpected API response structure:", result);
      }

      // Set models in both local state and global context
      const finalModels = Array.isArray(modelsData) ? modelsData : [];
      setModels(finalModels);
      setState((prev) => ({
        ...prev,
        models: finalModels,
      }));
    } catch (error) {
      console.error("Error fetching task data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch models for the selected task when taskId from URL changes
  useEffect(() => {
    if (taskId) {
      fetchTaskData(taskId);
    }
  }, [taskId, fetchTaskData]);

  // Sync local models with context models when context changes
  useEffect(() => {
    if (contextModels && contextModels.length > 0) {
      setModels(contextModels);
    }
  }, [contextModels]);

  // Reset filter and refresh data when component mounts or page changes
  useEffect(() => {
    // Reset filter to default (Not Started = 0)
    setState((prev) => ({
      ...prev,
      selected_status: 0,
      selected_model: null,
      selected_index: null,
    }));

    // Reset local states
    setCurrentPage(1);
    // 3(false);
    setSearchUid("");
  }, [setState]); // Empty dependency array means this runs on mount and when returning to the page

  // Verify task function for reviewers
  const handleVerifyTask = async () => {
    if (!taskId) {
      handleApiError(new Error("No task selected"));
      return;
    }

    setVerifyLoading(true);
    try {
      await tasksAPI.verifyTask(taskId);
      handleApiSuccess("Task verified successfully!");
      // Optionally navigate back to tasks or refresh data
      // navigate('/tasks');
    } catch (error) {
      console.error("Error verifying task:", error);
      handleApiError(error, "Failed to verify task or Task already Verified");
    } finally {
      setVerifyLoading(false);
    }
  };

  // Get task type from context or localStorage as fallback
  const taskType =
    selected_task_type || localStorage.getItem("current_task_type");

  // Function to handle model verification
  const handleVerifyModel = async (modelId, boolType) => {
    if (isVerifying) return;

    setIsVerifying(true);
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${BASE_URL_FLASK}/user/tasks/verify`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          bool_type: boolType,
          model_id: modelId,
          task_type: taskType,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        console.log("Model verified successfully:", result);

        // Check if the API returned success message
        if (result.message === "Model verified successfully") {
          if (boolType == true) {
            toast.success("Model verified successfully!");
          } else {
            toast.success("Model un-verified successfully!");
          }

          // Update the model state in context
          setState((prev) => {
            const updatedModels = prev.models.map((model) => {
              const currentModelId = model.uid || model.model_id;
              if (currentModelId === modelId) {
                if (taskType === "classification") {
                  return {
                    ...model,
                    classify_reviewed: boolType,
                    classify_rejected: false,
                    rejection_feedback: boolType
                      ? ""
                      : model.rejection_feedback,
                  };
                } else {
                  return {
                    ...model,
                    model_reviewed: boolType,
                    model_rejected: false,
                    rejection_feedback: boolType
                      ? ""
                      : model.rejection_feedback,
                  };
                }
              }
              return model;
            });

            // Also update selected_model
            let updatedSelectedModel = prev.selected_model;
            if (
              prev.selected_model &&
              (prev.selected_model.uid === modelId ||
                prev.selected_model.model_id === modelId)
            ) {
              if (taskType === "classification") {
                updatedSelectedModel = {
                  ...prev.selected_model,
                  classify_reviewed: boolType,
                  classify_rejected: false,
                  rejection_feedback: boolType
                    ? ""
                    : prev.selected_model.rejection_feedback,
                };
              } else {
                updatedSelectedModel = {
                  ...prev.selected_model,
                  model_reviewed: boolType,
                  model_rejected: false,
                  rejection_feedback: boolType
                    ? ""
                    : prev.selected_model.rejection_feedback,
                };
              }
            }

            return {
              ...prev,
              models: updatedModels,
              selected_model: updatedSelectedModel,
            };
          });
        } else {
          toast.error("An error occurred");
        }
      } else {
        const error = await response.json();
        console.error("Failed to verify model:", error);
        toast.error("An error occurred");
      }
    } catch (error) {
      console.error("Error verifying model:", error);
      toast.error("An error occurred");
    } finally {
      setIsVerifying(false);
    }
  };

  // Function to handle model rejection
  const handleRejectModel = async (modelId, feedback) => {
    if (isVerifying) return;

    setIsVerifying(true);
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${BASE_URL_FLASK}/user/tasks/reject`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          model_id: modelId,
          task_type: taskType,
          feedback: feedback,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        console.log("Model rejected successfully:", result);

        toast.success("Model rejected successfully!");

        // Update the model state in context
        setState((prev) => {
          const updatedModels = prev.models.map((model) => {
            const currentModelId = model.uid || model.model_id;
            if (currentModelId === modelId) {
              if (taskType === "classification") {
                return {
                  ...model,
                  classify_rejected: true,
                  classify_reviewed: false,
                  rejection_feedback: feedback,
                };
              } else {
                return {
                  ...model,
                  model_rejected: true,
                  model_reviewed: false,
                  rejection_feedback: feedback,
                };
              }
            }
            return model;
          });

          // Also update selected_model
          let updatedSelectedModel = prev.selected_model;
          if (
            prev.selected_model &&
            (prev.selected_model.uid === modelId ||
              prev.selected_model.model_id === modelId)
          ) {
            if (taskType === "classification") {
              updatedSelectedModel = {
                ...prev.selected_model,
                classify_rejected: true,
                classify_reviewed: false,
                rejection_feedback: feedback,
              };
            } else {
              updatedSelectedModel = {
                ...prev.selected_model,
                model_rejected: true,
                model_reviewed: false,
                rejection_feedback: feedback,
              };
            }
          }

          return {
            ...prev,
            models: updatedModels,
            selected_model: updatedSelectedModel,
          };
        });

        // Close modal and reset
        setShowRejectModal(false);
        setRejectFeedback("");
        setSelectedModelForReject(null);
      } else {
        const error = await response.json();
        console.error("Failed to reject model:", error);
        toast.error("Failed to reject model");
      }
    } catch (error) {
      console.error("Error rejecting model:", error);
      toast.error("An error occurred");
    } finally {
      setIsVerifying(false);
    }
  };

  const openRejectModal = (modelId) => {
    setSelectedModelForReject(modelId);
    setShowRejectModal(true);
  };

  const closeRejectModal = () => {
    setShowRejectModal(false);
    setRejectFeedback("");
    setSelectedModelForReject(null);
  };

  const submitRejection = () => {
    if (!rejectFeedback.trim()) {
      toast.error("Please provide feedback for rejection");
      return;
    }
    handleRejectModel(selectedModelForReject, rejectFeedback);
  };

  const getFilteredModels = () => {
    let filtered = models;
    if (selected_status !== "All") {
      filtered = filtered?.filter((model) => model.status === selected_status);
    }

    // Apply alignment/verification filter
    switch (alignmentFilter) {
      case "Un-Aligned":
        filtered = filtered?.filter((model) => !model.aligned);
        break;
      case "Aligned":
        filtered = filtered?.filter((model) => model.aligned);
        break;
      case "Verified":
        filtered = filtered?.filter((model) =>
          taskType === "classification"
            ? model.classify_reviewed === true
            : model.model_reviewed === true,
        );
        break;
      case "Not-Verified":
        filtered = filtered?.filter((model) =>
          taskType === "classification"
            ? model.classify_reviewed === false ||
              model.classify_reviewed == null
            : model.model_reviewed === false || model.model_reviewed == null,
        );
        break;
      case "Rejected":
        filtered = filtered?.filter((model) =>
          taskType === "classification"
            ? model.classify_rejected === true
            : model.model_rejected === true,
        );
        break;
      case "Not-Rejected":
        filtered = filtered?.filter((model) =>
          taskType === "classification"
            ? model.classify_rejected === false ||
              model.classify_rejected == null
            : model.model_rejected === false || model.model_rejected == null,
        );
        break;
      // "All" - no filtering needed
    }

    if (searchUid.trim() !== "") {
      // Split by comma, trim spaces, filter out empty strings
      const searchTerms = searchUid
        .split(",")
        .map((s) => s.trim().toLowerCase())
        .filter((s) => s.length > 0);
      if (searchTerms.length > 0) {
        filtered = filtered?.filter((model) =>
          searchTerms.some(
            (term) =>
              (model.uid && model.uid.toLowerCase().includes(term)) ||
              (model.model_id && model.model_id.toLowerCase().includes(term)),
          ),
        );
      }
    }
    return filtered;
  };

  const filtered_models = getFilteredModels();

  const [open, setOpen] = useState(false);

  const handleModelClick = (model) => {
    const modelId = model.uid || model.model_id;
    const index = filtered_models.findIndex(
      (item) => item.uid === modelId || item.model_id === modelId,
    );
    if (index !== -1) {
      setState((prev) => ({
        ...prev,
        selected_index: index,
        selected_model: model,
      }));
      setOpen(true);
    }
  };

  useEffect(() => {
    if (
      selected_index !== null &&
      (selected_index < 0 || selected_index >= filtered_models.length)
    ) {
      // Only close the ModelViewer if the selected model is no longer in filtered_models
      // This prevents closing when filters change due to model updates (like alignment changes)
      const selectedModelStillExists = filtered_models.some(
        (model) =>
          (model.uid && model.uid === selected_model?.uid) ||
          (model.model_id && model.model_id === selected_model?.model_id) ||
          model.uid === selected_model?.model_id ||
          model.model_id === selected_model?.uid,
      );

      if (!selectedModelStillExists) {
        setState((prev) => ({
          ...prev,
          selected_index: null,
        }));
        setOpen(false);
      } else {
        // If the model still exists but index is wrong, just update the index
        const newIndex = filtered_models.findIndex(
          (model) =>
            (model.uid && model.uid === selected_model?.uid) ||
            (model.model_id && model.model_id === selected_model?.model_id) ||
            model.uid === selected_model?.model_id ||
            model.model_id === selected_model?.uid,
        );
        setState((prev) => ({
          ...prev,
          selected_index: newIndex,
        }));
      }
    }
  }, [filtered_models, selected_index, selected_model, setState]);

  useEffect(() => {
    if (selected_model) {
      const updatedModel = models.find(
        (model) =>
          (model.uid && model.uid === selected_model.uid) ||
          (model.model_id && model.model_id === selected_model.model_id) ||
          model.uid === selected_model.model_id ||
          model.model_id === selected_model.uid,
      );
      if (updatedModel) {
        setState((prev) => ({
          ...prev,
          selected_model: updatedModel,
        }));
      }
    }
  }, [models, selected_model, setState]);

  const blogsPerPage = 120;
  const totalPages = Math.ceil((filtered_models?.length || 0) / blogsPerPage);
  const indexOfLastBlog = currentPage * blogsPerPage;
  const indexOfFirstBlog = indexOfLastBlog - blogsPerPage;
  const currentModels = (filtered_models || []).slice(
    indexOfFirstBlog,
    indexOfLastBlog,
  );

  // const getSignedUrl = async (path) => {
  //   const res = await fetch(`${BASE_URL_FLASK}/${path}`);
  //   const result = await res.text();
  //   return result;
  // };

  const handleDownloadCSV = async () => {
    if (!filtered_models || filtered_models.length === 0) return;
    setCsvLoading(true);
    setProgress(0);
    const header = "item_name,category,deliverable\n";
    const rows = filtered_models
      .map((model, idx) => {
        let item_name = model.uid || model.model_id;
        const prefix = "https://sketchfab.com/3d-models/";
        if (item_name.startsWith(prefix)) {
          item_name = item_name.slice(prefix.length);
        }
        return `${item_name},classification tool,3dmodels`;
      })
      .join("\n");
    const csvContent = header + rows;
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${selected_table || "models"}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setCsvLoading(false);
    setProgress(0);
  };

  // Show loading spinner when fetching data
  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        bg="#06090B"
      >
        <Box textAlign="center">
          <Spinner size="xl" color="#FF9900" thickness="4px" speed="0.65s" />
          <Box mt="4" color="#FF9900" fontSize="lg" fontWeight="medium">
            Loading models...
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <div className="relative flex flex-col color-white h-full w-full justify-start">
      <div className="absolute top-0 right-0 p-4">
        <div className="flex justify-end items-center w-full gap-4">
          {/* Verify Task Button - Only for Reviewers */}
          {userRole === "Reviewer" && taskId && (
            <Button
              leftIcon={<MdVerifiedUser />}
              bg="#10B981"
              color="white"
              _hover={{ bg: "#059669" }}
              onClick={handleVerifyTask}
              isLoading={verifyLoading}
              loadingText="Verifying..."
              size="md"
              my={4}
            >
              Verify Task
            </Button>
          )}
        </div>
        <div className="bg-[#15232B] rounded-lg px-4 py-2">
          <span className="font-bold sm:text-[24px] text-[18px] text-[#FF9900]">
            Models: {filtered_models?.length}
          </span>
        </div>
      </div>
      <div className="px-10 py-4">
        <h1 className="font-bold sm:text-[42px] text-[32px] text-[#FF9900]">
          3D Model Classification Tool
        </h1>
        <div className="mb-4">
          <ModelStatusFilter />
        </div>
        {/* Search bar for UID */}
        <div className="mb-4">
          <input
            type="text"
            placeholder="Search by Model IDs"
            value={searchUid}
            onChange={(e) => setSearchUid(e.target.value)}
            className="px-4 py-2 rounded-full border border-orange-300  bg-[#15232B] text-[#FF9900] w-full max-w-xl mb-2"
          />
        </div>
        {["2"].includes(String(selected_status)) ? (
          <div className="flex flex-col items-center gap-4">
            <button
              className={
                "inline-flex items-center gap-2 rounded-full py-1.5 px-3 text-sm/6 font-bold text-black shadow-inner shadow-white/10 focus:outline-none focus:outline-1 bg-[#FF9900]"
              }
              onClick={handleDownloadCSV}
              disabled={csvLoading}
            >
              {csvLoading ? "Creating CSV..." : "Download CSV"}
            </button>
            {csvLoading && (
              <div className="w-full max-w-xl mt-2">
                <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-2 bg-[#FF9900] transition-all duration-200"
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
                <div className="text-xs text-[#FF9900] mt-1">{progress}%</div>
              </div>
            )}
          </div>
        ) : (
          <></>
        )}
        {["1", "4", "5"].includes(String(selected_status)) ? (
          <div className="flex justify-center items-center mt-2">
            <Select
              value={alignmentFilter}
              onChange={(e) => setAlignmentFilter(e.target.value)}
              bg="#15232B"
              borderColor="orange.300"
              color="#FF9900"
              size="sm"
              width="200px"
              rounded={"md"}
              _hover={{ borderColor: "#FF9900" }}
              _focus={{
                borderColor: "#FF9900",
                boxShadow: "0 0 0 1px #FF9900",
              }}
            >
              <option
                value="All"
                style={{
                  backgroundColor: "#15232B",
                  color: "#FF9900",
                  rounded: "md",
                }}
              >
                All
              </option>
              <option
                value="Un-Aligned"
                style={{ backgroundColor: "#15232B", color: "#FF9900" }}
              >
                Un-Aligned
              </option>
              <option
                value="Aligned"
                style={{ backgroundColor: "#15232B", color: "#FF9900" }}
              >
                Aligned
              </option>
              <option
                value="Verified"
                style={{ backgroundColor: "#15232B", color: "#FF9900" }}
              >
                Verified
              </option>
              <option
                value="Not-Verified"
                style={{ backgroundColor: "#15232B", color: "#FF9900" }}
              >
                Not-Verified
              </option>
              <option
                value="Rejected"
                style={{ backgroundColor: "#15232B", color: "#FF9900" }}
              >
                Rejected
              </option>
              <option
                value="Not-Rejected"
                style={{ backgroundColor: "#15232B", color: "#FF9900" }}
              >
                Not-Rejected
              </option>
            </Select>
          </div>
        ) : (
          <></>
        )}
      </div>
      <div className="sm:mt-0 mt-[50px] w-full sm:px-16 px-6">
        <div className="border-t border-[#6F6F6F] mx-auto"></div>
      </div>
      <div className="h-[50vh] sm:px-20 p-4 sm:ml-0 ml-2 grid sm:grid-cols-5 gap-8 overflow-auto scrollbar-color">
        {currentModels?.map((item) => {
          const borderColor = item.aligned ? "#00FF00" : "#A0A3A6";
          const hoverBorderColor = item.aligned ? "#00FF00" : "#FF9900";

          // Handle thumbnail path for new data structure
          let thumbnailUrl;
          if (item.thumbnail_path) {
            // For new data structure with thumbnail_path
            thumbnailUrl = `${BASE_URL_FLASK}/${item.thumbnail_path}`;
          } else if (item.uid) {
            // Fallback for old data structure
            const basePath = item.uid?.split("-")[0];
            thumbnailUrl = `${BASE_URL_FLASK}/previews/${basePath}-1.jpg`;
          } else {
            // Default thumbnail
            thumbnailUrl = `${BASE_URL_FLASK}/default-thumbnail.jpg`;
          }

          // Add timestamp parameter if preview_updated_at exists
          const finalUrl = `${thumbnailUrl}${
            item.preview_updated_at ? `?t=${item.preview_updated_at}` : ""
          }`;

          // Use model_id or uid as the key
          const modelKey =
            item.model_id || item.uid || `model-${Math.random()}`;

          return (
            <div
              key={modelKey}
              onClick={() => handleModelClick(item)}
              className={`w-[275px] h-[275px] border cursor-pointer rounded-lg bg-black transform transition-transform duration-300 hover:scale-110`}
              style={{
                borderColor: borderColor,
                borderWidth: "1px",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = hoverBorderColor;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = borderColor;
              }}
            >
              <img
                src={finalUrl}
                alt="3D Model Preview"
                className="w-full h-full object-cover rounded-lg absolute"
                loading="lazy"
              />
              {(userRole === "Reviewer" ||
                (userRole === "Admin" &&
                  selected_task_type == "classification")) && (
                <div className="ml-4 p-4 h-full flex flex-col items-start relative justify-end gap-2">
                  {/* Display rejection status and feedback if model is rejected */}
                  {((taskType === "classification" &&
                    item?.classify_rejected) ||
                    (taskType === "alignment" && item?.model_rejected)) && (
                    <div className="bg-red-900/80 text-white text-xs px-2 py-1 rounded-md mb-2 max-w-[230px]">
                      <p className="font-bold">❌ Rejected</p>
                      {item?.rejection_feedback && (
                        <p className="mt-1 break-words">
                          {item.rejection_feedback}
                        </p>
                      )}
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id={`verify-checkbox-${modelKey}`}
                      className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500 focus:ring-2"
                      disabled={isVerifying}
                      checked={
                        taskType === "classification"
                          ? item?.classify_reviewed || false
                          : item?.model_reviewed || false
                      }
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => {
                        const modelId = item?.uid || item?.model_id;
                        const boolType = e.target.checked;
                        handleVerifyModel(modelId, boolType);
                      }}
                    />
                    <label
                      htmlFor={`verify-checkbox-${modelKey}`}
                      className={`text-sm ${
                        isVerifying ? "text-gray-400" : "text-green-400"
                      } cursor-pointer`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {isVerifying ? "Verifying..." : "Verify"}
                    </label>
                  </div>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const modelId = item?.uid || item?.model_id;
                      openRejectModal(modelId);
                    }}
                    disabled={isVerifying}
                    className="text-sm text-red-400 hover:text-red-300 disabled:text-gray-400 cursor-pointer bg-red-900/50 px-2 py-1 rounded"
                  >
                    {isVerifying ? "Processing..." : "Reject"}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="pt-4 flex justify-end sm:pr-10 pr-5 sm:pb-0 pb-4">
        <ResponsivePagination
          total={totalPages}
          current={currentPage}
          onPageChange={(page) => handlePageChange(page)}
        />
      </div>{" "}
      {open && selected_index !== null && (
        <ModelViewer
          setOpen={setOpen}
          models={filtered_models}
          onReload={() => fetchTaskData(taskId)}
        />
      )}
      {/* Rejection Modal */}
      {showRejectModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50"
          onClick={closeRejectModal}
        >
          <div
            className="bg-[#15232B] border border-[#FF9900] rounded-lg p-6 w-[90%] max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-2xl font-bold text-[#FF9900] mb-4">
              Reject Model
            </h2>
            <p className="text-white mb-4">
              Please provide feedback for rejecting this model:
            </p>
            <textarea
              className="w-full h-32 px-4 py-2 bg-[#0A1219] text-white border border-[#FF9900] rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF9900] mb-4"
              placeholder="Enter your feedback here..."
              value={rejectFeedback}
              onChange={(e) => setRejectFeedback(e.target.value)}
              autoFocus
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={closeRejectModal}
                disabled={isVerifying}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={submitRejection}
                disabled={isVerifying || !rejectFeedback.trim()}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-red-400 disabled:cursor-not-allowed"
              >
                {isVerifying ? "Rejecting..." : "Reject Model"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MainModels;
